import type { IRequestResponse, RecordSchema } from '@/types'

export interface ConsultationOverview {
	total_staff_consulted: number
	total_consultations: number
	completed_consultations: number
}

export interface ConsultationTrends {
	group: string
	completed: number
	cancelled: number
	pending: number
	total: number
}

export interface ConsultationActivities {
	id: string
	consultation_id: string
	employee_name: string
	label: string
	status: string
	description: string
	created_at: string
}
export interface GymSpaOverview {
	generated: number
	redeemed: number
	unique: number
}
export interface GymSpaTrends {
	group: number
	generated: number
	redeemed: number
}
export interface GymSpaTrendQuery {
	category: 'Gym' | 'Spa'
	purchase_id: string
	period?: 'quarter' | 'month' | 'week'
}
export interface GymSpaUsage {
	initiated: number
	redeemed: number
	usage_rate: number
}
export interface GymSpaHistory {
	employee: string
	facility: {
		name: string
	}
	date: string
	time: string
	status: string
	rating: number | null
}
export const useWellnessApi = () => {
	const getConsultationTrends = async (purchase_id: string, query?: { year?: number; group_by?: string }) => {
		const { data } = await useAPI<IRequestResponse<ConsultationTrends[]>>('/consultations/business/trends', {
			query: { ...query, purchase_id }
		})
		return data.value
	}
	const getConsultationOverview = async (purchase_id: string) => {
		const { data } = await useAPI<IRequestResponse<ConsultationOverview>>('/consultations/business/overview', {
			query: { purchase_id }
		})
		return data.value
	}
	const getConsultationActivities = async (purchase_id: string) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<ConsultationActivities[]>>>(
			'/consultations/business/activities',
			{
				query: { purchase_id }
			}
		)
		return data.value?.data
	}

	/**
     * @description Get gym or spa overview information
     @param query - Record<string, string | number | boolean | string[] | number[]> | undefined
     @example {category: 'Gym' | 'Spa'; start_date?: string; end_date?: string}
     @returns GymSpaOverview
     */
	const gymSpaOverview = async (query?: Partial<GymSpaTrendQuery>) => {
		const { data } = await useAPI<IRequestResponse<GymSpaOverview>>('/wellness/business/overview', { query })
		return data.value?.data
	}
	/**
     * @description Get gym or spa trends information
     @param query - GymSpaQuery
     @returns GymSpaTrends[]
     */
	const getGymSpaTrends = async (query?: Partial<GymSpaTrendQuery>) => {
		const { data } = await useAPI<IRequestResponse<GymSpaTrends[]>>('/wellness/business/trends', { query })
		return data.value?.data || []
	}
	const gymSpaUsage = async (query?: Partial<GymSpaTrendQuery>) => {
		const { data } = await useAPI<IRequestResponse<GymSpaUsage>>('/wellness/business/usage-rate', { query })
		return data.value?.data
	}

	/**
	 * @description Get gym or spa history information
	 * @param query - Record<string, string | number | boolean | string[] | number[]> | undefined
	 * @example {category: 'Gym' | 'Spa'; start_date?: string; end_date?: string}
	 * @returns GymSpaHistory
	 */
	const gymSpaHistory = async (q: RecordSchema<string>) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<GymSpaHistory[]>>>(
			'/wellness/business/history',
			{
				query: q
			}
		)
		return data.value?.data
	}
	return {
		gymSpaHistory,
		gymSpaUsage,
		gymSpaOverview,
		getGymSpaTrends,
		getConsultationTrends,
		getConsultationOverview,
		getConsultationActivities
	}
}
